import { Inject, Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { AddressFormBuilder } from '@domgen/dgx-fe-forms/address';
import { FormControlValidators } from '@domgen/dgx-fe-forms/shared';
import {
  COUNTRY_ISO_NUMBER,
  CountryIsoNumber,
  TranslateService,
} from '@domgen/dgx-fe-i18n';
import {
  ApplianceDetailsSteppedForm,
  ApplianceDetailsSteppedFormConfig,
  ApplianceDetailsSteppedFormField,
  ModelAndSerialNumberFormField,
  PersonalDetailsForm,
  PersonalDetailsFormField,
  RegistrationCodeField,
  RegistrationCodeForm,
} from '@us-registration/util-models';
import { startOfTomorrow } from 'date-fns';
import { FeatureApplianceDetailsSteppedTranslationDictionary } from '../../models/i18n/feature-appliance-details-stepped-dictionary.interface';

@Injectable()
export class ApplianceDetailsSteppedFormBuilder {
  constructor(
    @Inject(COUNTRY_ISO_NUMBER)
    private readonly countryIsoNumber: CountryIsoNumber,
    private readonly addressFormBuilder: AddressFormBuilder,
    private readonly formBuilder: FormBuilder,
    private readonly translateService: TranslateService<FeatureApplianceDetailsSteppedTranslationDictionary>
  ) {}

  buildApplianceDetailsSteppedForm(
    config: ApplianceDetailsSteppedFormConfig
  ): FormGroup<ApplianceDetailsSteppedForm> {
    return this.formBuilder.group<ApplianceDetailsSteppedForm>(
      this.buildApplianceDetailsSteppedFormConfig(config)
    );
  }

  private buildApplianceDetailsSteppedFormConfig(
    config: ApplianceDetailsSteppedFormConfig
  ): ApplianceDetailsSteppedForm {
    const initialValue = config.initialValue;
    const cmsData = config.cmsData;

    return {
      [ApplianceDetailsSteppedFormField.BrandCode]: this.formBuilder.control(
        config.clientBrandCode ?? initialValue.brandCode ?? null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'APPLIANCE_DETAILS_STEPPED_FORM_BRAND_CODE_ERROR_REQUIRED'
            )
          ),
        ])
      ),
      [ApplianceDetailsSteppedFormField.ApplianceCategoryCode]:
        this.formBuilder.control(
          initialValue.applianceCategoryCode ?? null,
          Validators.compose([
            FormControlValidators.requiredConditionally(
              Boolean(!initialValue.applianceCode),
              this.translateService.translate(
                'APPLIANCE_DETAILS_STEPPED_FORM_APPLIANCE_CATEGORY_CODE_ERROR_REQUIRED'
              )
            ),
          ])
        ),
      [ApplianceDetailsSteppedFormField.ApplianceCode]:
        this.formBuilder.control(
          initialValue.applianceCode ?? null,
          Validators.compose([
            FormControlValidators.required(
              this.translateService.translate(
                'APPLIANCE_DETAILS_STEPPED_FORM_APPLIANCE_CODE_ERROR_REQUIRED'
              )
            ),
          ])
        ),
      [ApplianceDetailsSteppedFormField.ModelAndSerialNumber]:
        this.formBuilder.group({
          [ModelAndSerialNumberFormField.ModelNumber]: this.formBuilder.control(
            {
              value: initialValue.modelAndSerialNumber?.modelNumber ?? null,
              disabled: Boolean(
                cmsData.modelAndSerialNumberFieldset.modelNumberField
                  .fieldHidden
              ),
            },
            Validators.compose([
              FormControlValidators.required(
                this.translateService.translate(
                  'APPLIANCE_DETAILS_STEPPED_FORM_MODEL_NUMBER_ERROR_REQUIRED'
                )
              ),
              FormControlValidators.maximumLength(
                20,
                this.translateService.translate(
                  'APPLIANCE_DETAILS_STEPPED_FORM_MODEL_NUMBER_ERROR_MAX_LENGTH',
                  {
                    maxLength: 20,
                  }
                )
              ),
            ])
          ),
          [ModelAndSerialNumberFormField.SerialNumber]:
            this.formBuilder.control(
              {
                value: initialValue.modelAndSerialNumber?.serialNumber ?? null,
                disabled: Boolean(
                  cmsData.modelAndSerialNumberFieldset.serialNumberField
                    .fieldHidden
                ),
              },
              Validators.compose([
                FormControlValidators.required(
                  this.translateService.translate(
                    'APPLIANCE_DETAILS_STEPPED_FORM_SERIAL_NUMBER_ERROR_REQUIRED'
                  )
                ),
                FormControlValidators.minimumLength(
                  9,
                  this.translateService.translate(
                    'APPLIANCE_DETAILS_STEPPED_FORM_SERIAL_NUMBER_ERROR_MIN_LENGTH'
                  )
                ),
                FormControlValidators.maximumLength(
                  20,
                  this.translateService.translate(
                    'APPLIANCE_DETAILS_STEPPED_FORM_SERIAL_NUMBER_ERROR_MAX_LENGTH'
                  )
                ),
              ])
            ),
        }),
      [ApplianceDetailsSteppedFormField.PurchasePrice]:
        this.formBuilder.control(
          initialValue.purchasePrice ?? null,
          Validators.compose([
            FormControlValidators.required(
              this.translateService.translate(
                'APPLIANCE_DETAILS_STEPPED_FORM_PURCHASE_PRICE_ERROR_REQUIRED'
              )
            ),
            FormControlValidators.minimum(
              1,
              this.translateService.translate(
                'APPLIANCE_DETAILS_STEPPED_FORM_PURCHASE_PRICE_ERROR_MIN_LENGTH'
              )
            ),
            FormControlValidators.maximum(
              99999.99,
              this.translateService.translate(
                'APPLIANCE_DETAILS_STEPPED_FORM_PURCHASE_PRICE_ERROR_MAX_LENGTH'
              )
            ),
          ])
        ),
      [ApplianceDetailsSteppedFormField.PurchaseDate]: this.formBuilder.control(
        initialValue.purchaseDate ?? null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'APPLIANCE_DETAILS_STEPPED_FORM_PURCHASE_DATE_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.afterDate(
            config.applianceMaximumAge,
            this.translateService.translate(
              'APPLIANCE_DETAILS_STEPPED_FORM_PURCHASE_DATE_ERROR_AFTER_DATE'
            )
          ),
          FormControlValidators.beforeDate(
            startOfTomorrow(),
            this.translateService.translate(
              'APPLIANCE_DETAILS_STEPPED_FORM_PURCHASE_DATE_ERROR_BEFORE_DATE'
            )
          ),
        ])
      ),

      [ApplianceDetailsSteppedFormField.PersonalDetails]:
        this.buildPersonalDetailsForm(),
    };
  }

  buildRegistrationCodeForm(): FormGroup<RegistrationCodeForm> {
    return this.formBuilder.group<RegistrationCodeForm>(
      this.buildRegistrationCodeFormConfig()
    );
  }

  buildPersonalDetailsForm(): FormGroup<PersonalDetailsForm> {
    return this.formBuilder.group<PersonalDetailsForm>(
      this.buildPersonalDetailsFormConfig()
    );
  }

  private buildRegistrationCodeFormConfig(): RegistrationCodeForm {
    return {
      [RegistrationCodeField.RegistrationCode]: this.formBuilder.control(
        {
          value: null,
          disabled: false,
        },
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'APPLIANCE_DETAILS_STEPPED_FORM_REGISTRATION_CODE_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.minimumLength(
            9,
            this.translateService.translate(
              'APPLIANCE_DETAILS_STEPPED_FORM_REGISTRATION_CODE_ERROR_MIN_LENGTH',
              {
                minLength: 9,
              }
            )
          ),
          FormControlValidators.maximumLength(
            10,
            this.translateService.translate(
              'APPLIANCE_DETAILS_STEPPED_FORM_REGISTRATION_CODE_ERROR_MAX_LENGTH',
              {
                maxLength: 10,
              }
            )
          ),
        ])
      ),
    };
  }

  private buildPersonalDetailsFormConfig(): PersonalDetailsForm {
    return {
      [PersonalDetailsFormField.Address]:
        this.addressFormBuilder.buildAddressOverrideForm(),
      [PersonalDetailsFormField.FirstName]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_FIRST_NAME_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.alphabetical(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_FIRST_NAME_ERROR_ALPHABETICAL'
            )
          ),
          FormControlValidators.maximumLength(
            15,
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_FIRST_NAME_ERROR_MAX_LENGTH',
              { maxLength: 15 }
            )
          ),
        ])
      ),
      [PersonalDetailsFormField.LastName]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_LAST_NAME_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.alphabetical(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_LAST_NAME_ERROR_ALPHABETICAL'
            )
          ),
          FormControlValidators.maximumLength(
            20,
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_LAST_NAME_ERROR_MAX_LENGTH',
              { maxLength: 20 }
            )
          ),
        ])
      ),
      [PersonalDetailsFormField.EmailAddress]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_EMAIL_ADDRESS_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.email(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_EMAIL_ADDRESS_ERROR_EMAIL'
            )
          ),
          FormControlValidators.maximumLength(
            50,
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_EMAIL_ADDRESS_ERROR_MAX_LENGTH',
              { maxLength: 50 }
            )
          ),
        ])
      ),
      [PersonalDetailsFormField.TelephoneNumber]: new FormControl(
        null,
        Validators.compose([
          FormControlValidators.required(
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_TELEPHONE_NUMBER_ERROR_REQUIRED'
            )
          ),
          FormControlValidators.telephone(
            this.countryIsoNumber,
            this.translateService.translate(
              'PERSONAL_DETAILS_FORM_TELEPHONE_NUMBER_ERROR_PATTERN'
            )
          ),
        ])
      ),
      [PersonalDetailsFormField.PrivacyPolicy]: new FormControl(
        {
          value: null,
          disabled: false,
        },
        {
          nonNullable: true,
          validators: Validators.compose([
            FormControlValidators.requiredTrue(
              this.translateService.translate(
                'PERSONAL_DETAILS_FORM_PRIVACY_POLICY_ERROR_REQUIRED'
              )
            ),
          ]),
        }
      ),
    };
  }
}
